import { useApi, useToaster } from "@shopexperts/shared";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { payoutRequestErrorMessage } from "./payoutRequestError";

interface DecidePayoutRequestInput {
  payoutRequestId: number;
  organizationId?: number | null;
  publicNotes?: string | null;
  internalNotes?: string | null;
}

export function useAdminPayoutRequestActions() {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  function invalidate(organizationId?: number | null) {
    queryClient.invalidateQueries({ queryKey: ["admin", "payout-requests"] });
    queryClient.invalidateQueries({ queryKey: ["admin", "wallet", "transactions"] });
    if (typeof organizationId === "number") {
      queryClient.invalidateQueries({ queryKey: ["admin", "wallet", "organization", organizationId] });
    }
  }

  const approve = useMutation({
    mutationKey: ["admin", "payout-requests", "approve"],
    mutationFn: async ({ payoutRequestId, publicNotes, internalNotes }: DecidePayoutRequestInput) => {
      return await api.admin.wallet.approvePayoutRequest({
        params: { payoutRequest: payoutRequestId },
        body: {
          public_notes: publicNotes ?? null,
          internal_notes: internalNotes ?? null,
        },
      });
    },
    onSuccess: (_request, variables) => {
      toaster.create({ title: "Payout request approved", type: "success" });
      invalidate(variables.organizationId);
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to approve payout request: ${payoutRequestErrorMessage(error)}`, type: "error" });
      queryClient.invalidateQueries({ queryKey: ["admin", "payout-requests"] });
    },
  });

  const reject = useMutation({
    mutationKey: ["admin", "payout-requests", "reject"],
    mutationFn: async ({ payoutRequestId, publicNotes, internalNotes }: DecidePayoutRequestInput) => {
      return await api.admin.wallet.rejectPayoutRequest({
        params: { payoutRequest: payoutRequestId },
        body: {
          public_notes: publicNotes ?? null,
          internal_notes: internalNotes ?? null,
        },
      });
    },
    onSuccess: (_request, variables) => {
      toaster.create({ title: "Payout request rejected", type: "success" });
      invalidate(variables.organizationId);
    },
    onError: (error: Error) => {
      toaster.create({ title: `Failed to reject payout request: ${payoutRequestErrorMessage(error)}`, type: "error" });
      queryClient.invalidateQueries({ queryKey: ["admin", "payout-requests"] });
    },
  });

  return { approve, reject };
}
